// =============================================================================
// HUB COMMERCIAL - Formulaire Création Dossier
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { isInstallateurProfile, defaultClientType,
         getDossierCreationProfile,
         getDossierActivite } from "../../utils/tenantProfile";
import { applyDetectedFormRef } from "../../lib/omvDossier";
import { useConfigList } from "../../hooks/useConfigList";
import { useSiretSearch, isLuhnValid } from "../../hooks/useSiretSearch";
import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";
import CreateDossierCore from "./CreateDossierCore";
import CreateDossierTefWizard from "./CreateDossierTefWizard";   // CC-DEV1-31 wizard TEF
import {
  ArrowLeft,
  Building2,
  Thermometer,
  User,
  Search,
  Plus,
  Check,
  RefreshCw,
  FolderPlus,
  X,
  Upload,
  FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
// Composants NORMÉS charte OmniHub (§4) — dérivés tokens var(--oh-*), dual-mode.
import {
  OhCard,
  OhCardHeader,
  OhStatusNote,
  OhField,
  OhInput,
  OhSelect,
} from "../../components/ui/oh-charte";
import { formatClientFullName } from "../../utils/clientFormat";
import { CEE_IMPORT_MAP } from "../../utils/ceeImportMap";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Hiérarchie boutons (charte §2, dérivée tokens dual-mode) ───────────────
//  primaire  : fond accent + texte contrasté  → 1 seul par écran (Créer)
//  secondaire: contour accent + texte accent   → actions importantes (Importer)
//  discret   : contour gris + texte secondaire  → actions mineures (Annuler)
// Appliqués sur la primitive <Button variant="outline"> pour maîtriser
// exactement les tokens (cf. gabarit MobileDALWizard).
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnSecondary = { background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" };
const btnDiscret = { background: "transparent", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" };

// CEE_IMPORT_MAP : mapping extraction → (section, champ). Source UNIQUE partagée
// avec OmvCeeEditor (formulaire prospect/régie) → importé en tête du module.

// Extrait un message d'erreur LISIBLE (string) de n'importe quelle forme de
// réponse FastAPI : `detail` string (400/500), liste de validation `[{msg}]`
// (422), ou objet anti-doublon `{message, error}` (409). Garantit qu'on ne
// passe JAMAIS un objet à React (sinon crash "Objects are not valid as a React
// child" — cause du bug "erreur à la sauvegarde du client" sur réimport CEE).
function errMsg(payload, fallback = "Une erreur est survenue.") {
  const d = payload?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || fallback;
  if (typeof d === "object") return d.message || d.error || fallback;
  return fallback;
}

// Inputs OMV définis au NIVEAU MODULE (identité stable → pas de perte de focus
// au re-render, cf. leçon MoneyInput / fichiers protégés).
function OmvInput({ label, value, onChange, type = "text", placeholder, ...rest }) {
  return (
    <OhField label={label}>
      <OhInput
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "À remplir"}
        {...rest}
      />
    </OhField>
  );
}
function OmvSelect({ label, value, onChange, options, ...rest }) {
  return (
    <OhField label={label}>
      <OhSelect value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest}>
        <option value="">À remplir</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </OhSelect>
    </OhField>
  );
}

export default function CreateDossier({ tenantId, preselectedClientId, preselectedActivite, onBack, onSuccess }) {
  const { items: civiliteOptions } = useConfigList("civilites");
  // Secteurs d'activité OMV (liste éditable per-tenant — Variables et Listes).
  const { items: secteurOptions } = useConfigList("secteurs_activite");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  
  // Form state
  const [selectedClient, setSelectedClient] = useState(null);
  const [activité, setActivite] = useState(preselectedActivite || "");
  const [notes, setNotes] = useState("");

  // ─── OMV (installateur CEE) — intake conditionnel gaté tenant=omv_france ───
  // Field-names spec SPEC-CEE-OMV-DOSSIER-2026-06-07 (schéma models.py 6e70d820f).
  // ⚠️ TROIS NOTIONS, TROIS NOMS — elles étaient confondues sur une ligne.
  //   · isOMV ............. le BUSINESS MODEL (ce que le tenant VEND) : pilote
  //                         la SAISIE (SIREN, entreprise). Inchangé.
  //   · parcoursCreation .. le PARCOURS DÉCLARÉ (ce qu'il SUIT) : pilote QUEL
  //                         écran s'affiche. ⛔ `null` = non déclaré.
  const isOMV = isInstallateurProfile();
  const parcoursCreation = getDossierCreationProfile();
  const activiteDeclaree = getDossierActivite();
  const [omv, setOmv] = useState({
    // numero_dossier : n° de série MANUEL (Solène), saisi via la fiche après
    // création — JAMAIS pré-rempli ici (cf. AUDIT numero_dossier 2026-06-12).
    entreprise: { raison_sociale: "", secteur_activite: "", siret: "", adresse: "", cp: "", ville: "" },
    representant: { nom: "", prenom: "", fonction: "", email: "", tel: "", portable: "" },
    batiment: { type_batiment: "", plus_2_ans: false, surface_ventilee_m2: "", nb_logements: "", adresse_travaux: "", cp_travaux: "", ville_travaux: "", nom_etablissement: "" },
    technique: { type_vmc: "", nb_caissons: "" },
    cee: { code_fiche: "", operation_cee: "", montant_prime_cee: "", delegataire_nom: "", reference_formulaire: "" },
  });
  const setOmvField = (section, key, val) =>
    setOmv(o => ({ ...o, [section]: { ...o[section], [key]: val } }));

  // Recherche SIREN/SIRET (hook partagé, même API + debounce 500ms que l'onboarding
  // installateur) → pré-remplit l'entreprise bénéficiaire. Fallback : si introuvable
  // / API down, les champs restent saisissables manuellement (non bloquant).
  const { searching: siretSearching, info: siretInfo, searchDebounced: searchSiretDebounced } = useSiretSearch();
  // Erreur de clé de contrôle SIRET (filet OCR Vision). Champ surligné + éditable.
  const [siretError, setSiretError] = useState(null);
  const handleOmvSiretChange = (val) => {
    setOmvField("entreprise", "siret", val);
    const clean = (val || "").replace(/\s+/g, "");
    // Un SIRET/SIREN COMPLET mais à clé Luhn invalide = lecture erronée (ex. 9 lu 3).
    // On alerte + on N'interroge PAS SIRENE (numéro inexistant) ; le champ reste
    // éditable → l'enrichissement repart dès que le numéro corrigé redevient valide.
    if ((clean.length === 9 || clean.length === 14) && !isLuhnValid(clean)) {
      setSiretError("SIRET invalide (clé de contrôle) — vérifiez ce numéro (erreur de lecture probable).");
      return;
    }
    setSiretError(null);
    searchSiretDebounced(val, (m) => {
      if (!m) return;
      // Bascule SIÈGE / ÉTABLISSEMENT (cf. mapEntreprise.est_siege) :
      //  - est_siege=true  → bénéficiaire ET site = mêmes données (l'établissement
      //    interrogé EST le siège → m.adresse == adresse du siège).
      //  - est_siege=false → BÉNÉFICIAIRE = adresse du SIÈGE (m.siege_*) ;
      //    SITE/chantier = adresse de l'ÉTABLISSEMENT interrogé (m.adresse).
      // Le NOM du site vient TOUJOURS du formulaire (CEE_IMPORT_MAP) — jamais de
      // SIRENE ici (pas de repli sur m.nom_etablissement).
      // RUE SEULE dans le champ Adresse (jamais le bloc complet : CP/ville ont
      // leurs propres champs — fix irritant Solène, cf. splitFrenchAddress).
      const benefAdresse = m.est_siege ? m.rue : (m.siege_rue || m.rue);
      const benefCp = m.est_siege ? m.code_postal : (m.siege_cp || m.code_postal);
      const benefVille = m.est_siege ? m.ville : (m.siege_ville || m.ville);
      setOmv(o => ({
        ...o,
        entreprise: {
          ...o.entreprise,
          siret: m.siret || val,
          // pré-remplit uniquement les champs vides (n'écrase pas une saisie humaine)
          raison_sociale: o.entreprise.raison_sociale || m.raison_sociale,
          adresse: o.entreprise.adresse || benefAdresse,
          cp: o.entreprise.cp || benefCp,
          ville: o.entreprise.ville || benefVille,
        },
        // SITE = établissement interrogé (≠ siège), SEULEMENT si le formulaire ne
        // l'a pas déjà rempli (la passe CEE_IMPORT_MAP s'exécute avant ce callback
        // debounce). Pilote la zone climatique (cumac). nom_etablissement : non
        // touché ici — c'est le formulaire (CEE_IMPORT_MAP) qui le pose.
        batiment: {
          ...o.batiment,
          adresse_travaux: o.batiment.adresse_travaux || m.rue || "",
          cp_travaux: o.batiment.cp_travaux || m.code_postal || "",
          ville_travaux: o.batiment.ville_travaux || m.ville || "",
        },
      }));
    });
  };

  // ─── Import formulaire CEE (Phase 1 : PDF natif) ───────────────────────────
  // Upload → POST /api/metier/omv/cee/extract-form (gate omv_france) → merge SANS
  // écraser une saisie. siret_detecte → SIREN (entreprise = registre officiel).
  // Tout champ Vision/extrait = "à confirmer" (Solène relit). La réf formulaire
  // détectée va dans cee.reference_formulaire, jamais dans numero_dossier (n°
  // série manuel). L'endpoint n'écrit RIEN en DB (suggestion).
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importInfo, setImportInfo] = useState(null);   // {type:"scan"|"ok", count, fields:[{label,value,confirmed}]}
  // D2-B — on RETIENT le fichier formulaire importé pour l'archiver dans Zoho
  // (03_CEE) APRÈS création du dossier. extract-form ne fait que le LIRE (le
  // backend le jette) → sans ce ref, le PDF d'origine serait perdu.
  const [formFile, setFormFile] = useState(null);

  const applyExtraction = (data) => {
    // 0) Anti-duplication CP : Vision met parfois l'adresse COMPLÈTE dans
    // batiment_adresse_travaux alors que cp/ville sont des champs séparés. On
    // découpe adresse = rue / cp / ville pour ne pas répéter le CP (ni la ville).
    // Mutation in-place → le merge et l'aperçu en aval reprennent les valeurs nettoyées.
    {
      let rue = String(data.batiment_adresse_travaux || "");
      let cp = data.batiment_cp_travaux ? String(data.batiment_cp_travaux) : "";
      let ville = data.batiment_ville_travaux ? String(data.batiment_ville_travaux) : "";
      const cpInAdr = (rue.match(/\b(\d{5})\b/) || [])[1] || "";
      if (!cp && cpInAdr) cp = cpInAdr;                              // CP absent → extrait de l'adresse
      if (!ville && cpInAdr) ville = (rue.split(cpInAdr)[1] || "").replace(/^[\s,;-]+/, "").trim();  // ville = ce qui suit le CP
      if (cp) rue = rue.replace(new RegExp(`\\b${cp}\\b`, "g"), " ");
      if (ville) rue = rue.replace(new RegExp(ville.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig"), " ");
      rue = rue.replace(/[,;]/g, " ").replace(/\s{2,}/g, " ").replace(/^[\s,;-]+|[\s,;-]+$/g, "").trim();
      data.batiment_adresse_travaux = rue || null;
      data.batiment_cp_travaux = cp || null;
      data.batiment_ville_travaux = ville || null;
    }
    // 1) entreprise via SIREN (amorce siret_detecte) — autoritaire, "confirmé".
    const preview = [];
    if (data.siret_detecte) {
      handleOmvSiretChange(data.siret_detecte);   // pose + valide Luhn + enrichit si OK
      const ok = isLuhnValid(data.siret_detecte);
      preview.push({ label: ok ? "SIRET → SIREN" : "SIRET (à corriger)", value: data.siret_detecte, confirmed: ok });
    }
    // 2) merge des autres blocs sans écraser une valeur déjà saisie.
    setOmv(o => {
      const next = {
        ...o,
        representant: { ...o.representant },
        batiment: { ...o.batiment },
        technique: { ...o.technique },
        cee: { ...o.cee },
      };
      for (const [key, section, field, , isBool] of CEE_IMPORT_MAP) {
        const v = data[key];
        if (v === null || v === undefined || v === "") continue;
        const cur = next[section][field];
        const empty = isBool ? (cur === false || cur === "" || cur == null) : (cur === "" || cur == null);
        if (!empty) continue;
        next[section][field] = isBool ? !!v : String(v);
      }
      // Réf. formulaire CEE (n° lu par Vision sur le doc délégataire) : rangée
      // dans cee.reference_formulaire (NON-identifiant), proposition seulement,
      // JAMAIS d'écrasement. N'alimente PLUS numero_dossier (n° série manuel
      // Solène) — cf. AUDIT source réelle numero_dossier 2026-06-12.
      next.cee = applyDetectedFormRef(next.cee, data.numero_dossier_detecte);
      return next;
    });
    // 3) aperçu "à confirmer" (calculé depuis l'extraction brute).
    for (const [key, , , label] of CEE_IMPORT_MAP) {
      const v = data[key];
      if (v === null || v === undefined || v === "") continue;
      preview.push({ label, value: typeof v === "boolean" ? (v ? "Oui" : "Non") : String(v) });
    }
    if (data.numero_dossier_detecte) {
      preview.push({ label: "Réf. formulaire (CEE)", value: String(data.numero_dossier_detecte) });
    }
    setImportInfo({ type: "ok", count: preview.length, fields: preview });
  };

  const handleImportForm = async (file) => {
    if (!file) return;
    setFormFile(file);   // D2-B — retenu pour push Zoho 03_CEE post-création
    setImporting(true);
    setCreateError(null);
    setImportInfo(null);
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/metier/omv/cee/extract-form`, {
        method: "POST",
        credentials: "include",
        // PAS de Content-Type : le navigateur pose le boundary multipart.
        headers: {
          ...enteteTenant(tenantId || localStorage.getItem("current_tenant_slug")),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setCreateError(e?.detail || `Extraction du formulaire échouée (${res.status}).`);
        return;
      }
      const data = await res.json();
      if (data?._meta?.source === "needs_vision") {
        setImportInfo({ type: "scan" });
        return;
      }
      applyExtraction(data);
    } catch (err) {
      setCreateError("Erreur réseau pendant l'import du formulaire.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Modal création prospect
  const [showNewProspectModal, setShowNewProspectModal] = useState(false);
  const [newProspect, setNewProspect] = useState({
    civilite: "M.",
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    type_client: defaultClientType()  // preset segment tenant (OMV b2b → professionnel)
  });
  const [creatingProspect, setCreatingProspect] = useState(false);

  // Détection doublons (client + dossier)
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    mode: "client",
    matches: [],
    onConfirm: null,
  });

  // Builder de headers d'auth (factorisé)
  const buildAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [tenantId]);

  // Vérif doublon client avant création (useCallback AVANT useEffect)
  const checkClientDuplicate = useCallback(async (payload) => {
    try {
      const params = new URLSearchParams({ nom: payload.nom || "" });
      if (payload.prenom) params.append("prenom", payload.prenom);
      if (payload.code_postal) params.append("cp", payload.code_postal);
      const res = await fetch(`${API}/api/metier/clients/check-duplicate?${params}`, {
        headers: buildAuthHeaders(),
        credentials: "include",
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.matches || [];
    } catch (err) {
      console.error("check-duplicate client:", err);
      return [];
    }
  }, [buildAuthHeaders]);

  // Vérif doublon dossier avant création
  const checkDossierDuplicate = useCallback(async (clientId) => {
    try {
      const res = await fetch(
        `${API}/api/metier/dossiers/check-duplicate-by-client?client_id=${encodeURIComponent(clientId)}`,
        { headers: buildAuthHeaders(), credentials: "include" },
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.matches || [];
    } catch (err) {
      console.error("check-duplicate dossier:", err);
      return [];
    }
  }, [buildAuthHeaders]);

  useEffect(() => {
    loadInitialData();
  }, [tenantId, preselectedClientId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 
        ...enteteTenant(tenantId),
        ...(token && { "Authorization": `Bearer ${token}` })
      };

      // Charger tenant info
      const tenantRes = await fetch(`${API}/api/metier/tenant`, { headers, credentials: 'include' });
      if (tenantRes.ok) {
        const data = await tenantRes.json();
        setTenantInfo(data);
        
        // Auto-sélection activité si une seule disponible
        if (data.activites_disponibles?.tef && !data.activites_disponibles?.climat) {
          setActivite("tef");
        } else if (!data.activites_disponibles?.tef && data.activites_disponibles?.climat) {
          setActivite("climat");
        }
      }

      // Si client présélectionné, le charger
      if (preselectedClientId) {
        const clientRes = await fetch(`${API}/api/metier/clients/${preselectedClientId}`, { headers, credentials: 'include' });
        if (clientRes.ok) {
          setSelectedClient(await clientRes.json());
        }
      }
    } catch (err) {
      console.error("Erreur chargement:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const headers = { 
        ...enteteTenant(tenantId),
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      const res = await fetch(`${API}/api/metier/clients/search?q=${encodeURIComponent(query)}&limit=5`, { headers, credentials: 'include' });
      if (res.ok) {
        setSearchResults(await res.json());
      }
    } catch (err) {
      console.error("Erreur recherche:", err);
    }
  };

  // Étape 2 — crée le DOSSIER pour un client résolu (existant ou créé à la volée),
  // en y portant l'intake OMV (entreprise/représentant/bâtiment/technique/CEE).
  // Factorisé pour être réutilisable depuis le chemin "réutiliser le client
  // existant" du dialog anti-doublon (CC-41). Gère lui-même setCreating(false).
  // D2-B — pousse le formulaire CEE retenu (formFile) dans Zoho 03_CEE via
  // l'endpoint dédié post-création. Best-effort : toute erreur est avalée
  // (log console only) — la création/navigation du dossier prime.
  const pushFormDocument = async (dossierId) => {
    if (!formFile || !dossierId) return;
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", formFile);
      const res = await fetch(`${API}/api/metier/omv/cee/save-form-document/${dossierId}`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...enteteTenant(tenantId || localStorage.getItem("current_tenant_slug")),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: fd,
      });
      if (!res.ok) {
        console.warn("[OMV] archivage formulaire CEE Zoho échoué", res.status);
      }
    } catch (err) {
      console.warn("[OMV] archivage formulaire CEE Zoho — erreur réseau", err);
    }
  };

  const finalizeDossier = async (clientId) => {
    let omvPayload = {};
    if (isOMV) {
      const clean = (obj) => {
        const o = {};
        for (const [k, v] of Object.entries(obj)) if (v !== "" && v != null) o[k] = v;
        return Object.keys(o).length ? o : null;
      };
      omvPayload = {
        entreprise: clean(omv.entreprise),
        representant: clean(omv.representant),
        batiment: clean(omv.batiment),
        technique: clean(omv.technique),
        cee: clean(omv.cee),
        // numero_dossier NON envoyé pour OMV : reste manuel-only (édition fiche).
        // Le backend l'ignore de toute façon pour omv_france (defense-in-depth).
      };
      // Retirer les sections nulles pour ne pas polluer le payload.
      for (const k of Object.keys(omvPayload)) if (omvPayload[k] == null) delete omvPayload[k];
    }
    try {
      const res = await fetch(`${API}/api/metier/dossiers`, {
        method: "POST",
        headers: buildAuthHeaders(),
        credentials: "include",
        body: JSON.stringify({
          activite: activité,
          client_id: clientId,
          notes,
          ...omvPayload,
        }),
      });
      if (res.ok) {
        const newDossier = await res.json();
        // D2-B — archiver le formulaire CEE d'origine dans le dossier Zoho
        // (03_CEE). Best-effort STRICT : un échec ne bloque jamais la création
        // ni la navigation (le backend enqueue un repair si Zoho est KO).
        if (isOMV && formFile && newDossier?.id) {
          await pushFormDocument(newDossier.id);
        }
        onSuccess?.(newDossier);
      } else {
        const err = await res.json().catch(() => ({}));
        setCreateError(errMsg(err, `Erreur ${res.status} — impossible de créer le dossier.`));
      }
    } catch (err) {
      console.error("Erreur création dossier:", err);
      setCreateError("Erreur réseau — vérifiez votre connexion.");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDossier = async (forceCreate = false, forceClient = false, forceSiren = false) => {
    if (!activité) return;
    // OMV : le client peut être créé à la volée depuis l'entreprise (SIREN) → pas de
    // client présélectionné requis (écran création épuré = recherche nom OU entreprise).
    const hasOmvEntreprise = isOMV && !!(omv.entreprise.raison_sociale || omv.entreprise.siret);
    if (!selectedClient && !hasOmvEntreprise) return;

    // Vérif doublon dossier seulement si un client est déjà connu (sauf forceCreate).
    if (!forceCreate && selectedClient) {
      const existing = await checkDossierDuplicate(selectedClient.id);
      if (existing.length > 0) {
        setDuplicateDialog({
          open: true,
          mode: "dossier",
          matches: existing,
          onConfirm: () => handleCreateDossier(true),
          onUseExisting: (dossier) => {
            setDuplicateDialog({ open: false, mode: "dossier", matches: [], onConfirm: null });
            onSuccess?.({ id: dossier.id, reference: dossier.reference, activite: dossier.activite });
          },
        });
        return;
      }
    }

    setCreating(true);
    setCreateError(null);

    // SIREN-soft (OMV, non bloquant) : avant de créer, on AVERTIT si un dossier
    // existe déjà pour la MÊME entreprise (SIREN = 9 premiers chiffres) mais un
    // SIRET DIFFÉRENT (siège ≠ établissement, cas EVRON 00007/00044). Le 409
    // SIRET-exact (POST /clients ci-dessous) reste, lui, strict/bloquant. Le
    // multi-établissement légitime reste possible ("Créer un nouvel établissement").
    if (!forceSiren && isOMV && !selectedClient && hasOmvEntreprise) {
      const siretClean = (omv.entreprise.siret || "").replace(/\D/g, "");
      const siren = siretClean.slice(0, 9);
      if (siren.length === 9) {
        try {
          const r = await fetch(
            `${API}/api/metier/clients/by-siren?siren=${encodeURIComponent(siren)}`,
            { headers: buildAuthHeaders(), credentials: "include" },
          );
          if (r.ok) {
            const data = await r.json();
            // Établissements du même SIREN mais SIRET ≠ (le SIRET identique relève
            // du 409 strict, pas de l'avertissement soft) → liste des dossiers.
            const softDossiers = (data.matches || [])
              .filter(m => (m.siret || "").replace(/\D/g, "") !== siretClean)
              .flatMap(m => (m.dossiers || []).map(d => ({
                ...d, id: d.dossier_id, raison_sociale: m.raison_sociale, siret: m.siret,
              })));
            if (softDossiers.length > 0) {
              setCreating(false);
              setDuplicateDialog({
                open: true,
                mode: "dossier",
                matches: softDossiers,
                title: "Entreprise déjà connue (même SIREN)",
                description: `Un dossier existe déjà pour cette entreprise (SIREN ${siren}), sur un autre établissement (SIRET différent). Vous pouvez ouvrir un dossier existant, ou créer un nouvel établissement.`,
                createLabel: "Créer un nouvel établissement",
                useLabel: "Ouvrir ce dossier",
                onConfirm: () => handleCreateDossier(forceCreate, forceClient, true),
                onUseExisting: (d) => {
                  setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
                  onSuccess?.({ id: d.dossier_id || d.id, reference: d.reference, activite: d.activite });
                },
              });
              return;
            }
          }
        } catch (e) { console.warn("[OMV] by-siren soft échec (non bloquant)", e); }
      }
    }

    // 1) Résoudre le client : sélectionné, ou créé à la volée depuis l'entreprise (OMV).
    let clientId = selectedClient?.id;
    if (!clientId && hasOmvEntreprise) {
      const ent = omv.entreprise;
      let cRes;
      let cResBody = {};   // corps parsé UNE FOIS (réutilisé : 409 / !ok / succès)
      try {
        cRes = await fetch(`${API}/api/metier/clients${forceClient ? "?force=true" : ""}`, {
          method: "POST", headers: buildAuthHeaders(), credentials: "include",
          body: JSON.stringify({
            nom: ent.raison_sociale || ent.siret || "Entreprise",
            raison_sociale: ent.raison_sociale || null,
            type_client: "professionnel",
            siret: ent.siret || null,
            // adresse = objet structuré (Adresse), pas une chaîne
            adresse: { rue: ent.adresse || "", code_postal: ent.cp || "", ville: ent.ville || "" },
          }),
        });
        // Corps lu best-effort pour les chemins SUCCÈS (client_id) et ERREUR
        // (message). Le 409 ne s'appuie PLUS dessus (cf. branche dédiée) : son
        // corps est consommé par la protection web externe (dmn_chk) → illisible.
        try {
          const _txt = await cRes.clone().text();
          cResBody = _txt ? JSON.parse(_txt) : {};
        } catch (_e) {
          try {
            const _t = await cRes.text();
            cResBody = _t ? JSON.parse(_t) : {};
          } catch (_e2) { /* corps illisible — toléré, voir branche 409 */ }
        }
      } catch (err) {
        console.error("Erreur réseau création entreprise:", err);
        setCreateError("Erreur réseau — vérifiez votre connexion.");
        setCreating(false);
        return;
      }

      // CC-41 anti-doublon — CONTOURNEMENT protection web (dmn_chk) :
      // le body du 409 est consommé hors-code → illisible. On NE le lit PAS.
      // On s'appuie sur le seul STATUT 409 (déterministe : ne se déclenche que
      // sur SIRET 14 ch exactement identique, cf. POST /clients) → on retrouve
      // le client via GET /clients/by-siret (body GET non intercepté), puis son
      // dossier actif. Indépendant du clone().text() qui échouait → bouton actif.
      if (cRes.status === 409) {
        const siretClean = (ent.siret || "").replace(/\s/g, "");
        let resolved = null;   // {client_id, reference}
        if (siretClean) {
          try {
            const r = await fetch(
              `${API}/api/metier/clients/by-siret?siret=${encodeURIComponent(siretClean)}`,
              { headers: buildAuthHeaders(), credentials: "include" },
            );
            if (r.ok) resolved = await r.json();
            else console.warn("[OMV] by-siret non résolu", r.status);
          } catch (e) {
            console.warn("[OMV] by-siret erreur réseau", e);
          }
        }

        // Un seul match : le client existant retrouvé par SIRET (statut 409).
        const matches = resolved?.client_id ? [{
          id: resolved.client_id,
          nom: ent.raison_sociale || "",
          prenom: "",
          email: "",
          telephone: "",
          adresse: { code_postal: ent.cp || "", ville: ent.ville || "" },
          reference: resolved.reference,
          score: 1.0,
          match_reasons: ["SIRET identique"],
          dossiers_actifs_count: 0,
        }] : [];

        setDuplicateDialog({
          open: true,
          mode: "client",
          matches,
          // "Créer quand même" → relance avec ?force=true sur le client.
          onConfirm: () => handleCreateDossier(forceCreate, true),
          // "Utiliser le dossier" → en OMV, client = dossier (1:1). On OUVRE le
          // dossier existant rattaché au client doublon plutôt que d'en recréer un.
          // Repli : client orphelin (dossier supprimé) → on rattache l'intake.
          onUseExisting: async (m) => {
            setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
            setCreateError(null);
            setCreating(true);
            try {
              const existing = await checkDossierDuplicate(m.id);
              // Prendre le 1er dossier ACTIF (gérer le pluriel) ; repli = le plus
              // récent (tri created_at desc côté back) si aucun n'est actif.
              const d = existing.find((x) => x.is_active) || existing[0];
              if (d) {
                setCreating(false);
                onSuccess?.({ id: d.id, reference: d.reference, activite: d.activite });
                return;
              }
            } catch (err) {
              console.error("[OMV] résolution dossier existant échouée:", err);
            }
            finalizeDossier(m.id);
          },
        });
        // Message de repli : le 409 dédup SIRET reste lisible même si le dialog
        // ne s'affichait pas (état/rendu).
        setCreateError("Un client avec ce SIRET existe déjà — réutilisez-le ou forcez la création.");
        setCreating(false);
        return;
      }

      if (!cRes.ok) {
        setCreateError(errMsg(cResBody, "Erreur création de l'entreprise."));
        setCreating(false);
        return;
      }

      clientId = cResBody.client_id || cResBody.id;
    }

    // 2) Créer le dossier (porte l'intake OMV si présent).
    await finalizeDossier(clientId);
  };

  const handleCreateProspect = async (forceCreate = false) => {
    if (!newProspect.nom) return;

    // Vérif doublon client
    if (!forceCreate) {
      const existing = await checkClientDuplicate(newProspect);
      if (existing.length > 0) {
        setDuplicateDialog({
          open: true,
          mode: "client",
          matches: existing,
          onConfirm: () => handleCreateProspect(true),
          onUseExisting: (client) => {
            setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
            setSelectedClient(client);
            setShowNewProspectModal(false);
            setNewProspect({
              civilite: "M.", nom: "", prenom: "",
              email: "", telephone: "", type_client: defaultClientType(),
            });
          },
        });
        return;
      }
    }

    setCreatingProspect(true);
    try {
      const url = `${API}/api/metier/clients${forceCreate ? "?force=true" : ""}`;
      const res = await fetch(url, {
        method: "POST",
        headers: buildAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(newProspect),
      });

      if (res.ok) {
        const createdClient = await res.json();
        setSelectedClient(createdClient);
        setShowNewProspectModal(false);
        setNewProspect({
          civilite: "M.",
          nom: "",
          prenom: "",
          email: "",
          telephone: "",
          type_client: defaultClientType()  // preset segment tenant
        });
      } else if (res.status === 409) {
        // CC-41 V1 — backend a détecté des doublons fuzzy non vus côté UI strict.
        // Adapter le shape candidates → matches pour le dialog.
        const err = await res.json().catch(() => ({}));
        const candidates = err?.detail?.candidates || [];
        const matches = candidates.map((c) => ({
          id: c.client_id,
          nom: c.nom,
          prenom: c.prenom,
          email: c.email || "",
          telephone: "",
          adresse: { code_postal: c.code_postal, ville: c.ville },
          reference: c.reference,
          anah_reference: c.anah_reference,
          score: c.score,
          match_reasons: c.match_reasons || [],
          dossiers_actifs_count: 0,
        }));
        setDuplicateDialog({
          open: true,
          mode: "client",
          matches,
          onConfirm: () => handleCreateProspect(true),
          onUseExisting: async (m) => {
            try {
              const r = await fetch(`${API}/api/metier/clients/${m.id}`, {
                headers: buildAuthHeaders(), credentials: "include",
              });
              if (r.ok) setSelectedClient(await r.json());
            } catch (_) {}
            setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
            setShowNewProspectModal(false);
            setNewProspect({
              civilite: "M.", nom: "", prenom: "",
              email: "", telephone: "", type_client: defaultClientType(),
            });
          },
        });
      }
    } catch (err) {
      console.error("Erreur création prospect:", err);
    } finally {
      setCreatingProspect(false);
    }
  };

  const hasTEF = tenantInfo?.activites_disponibles?.tef;
  const hasClimat = tenantInfo?.activites_disponibles?.climat;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  // ─── WIZARD TEF/AMO (CC-DEV1-31) — vrai stepper 4 étapes (Bénéficiaire → Logement →
  //     MaPrimeAdapt' → Documents). Remplace l'ancien écran à étapes factices. OMV garde
  //     son écran épuré ci-dessous (gating isOMV inchangé). ───
  // 🔴 LE PARCOURS EST CELUI QUE LE TENANT A DÉCLARÉ — ⛔ PLUS « tout ce qui
  //    n'est pas OMV est TEF ».
  // ⚠️ MESURÉ AVANT CORRECTION, sur les 6 tenants : QUATRE recevaient un
  //    parcours métier qu'ils ⛔ n'avaient JAMAIS déclaré — `jkc` et `demo`
  //    (AUCUN business_model) et `mario_bat` tombaient dans le wizard AMO ANAH ;
  //    `climatpac` tombait dans l'écran OMV. ⛔ Un défaut de configuration
  //    devenait une DÉCISION MÉTIER SILENCIEUSE.
  // 🔑 `null` ⛔ n'est PAS une panne : c'est « non déclaré », et l'écran le DIT.
  //    Ajouter un vertical demain = DÉCLARER le champ côté SERVEUR, ⛔ PAS
  //    ajouter un `if` de plus ici.
  // 🔴 LE PARCOURS **CORE** — le socle OmniHub, ⛔ aucun champ métier.
  // ⚠️ Mesuré : le modèle `Dossier` a UN SEUL champ obligatoire (`activite`) et
  // ⛔ AUCUN champ TEF requis. Ce ⛔ n'était PAS le modèle qui imposait TEF —
  // c'était L'ÉCRAN. ⇒ On ouvre le CORE tel qu'il est, ⛔ on ⛔ ne crée PAS un
  // `DossierMario` qui serait un silo de plus.
  if (parcoursCreation === "core") {
    return (
      <CreateDossierCore
        tenantId={tenantId}
        activite={activiteDeclaree}
        clientPreselectionneId={preselectedClientId}
        onBack={onBack}
        onSuccess={onSuccess}
      />
    );
  }

  // 🔴 CLI-16 — DEUX PARCOURS, UN SEUL ASSISTANT, et le PROFIL EST ENFIN PASSÉ.
  //
  // ⚠️ LE DÉFAUT CORRIGÉ ICI : `CreateDossierTefWizard` filtrait DÉJÀ ses étapes
  //    par `profile` — mais ⛔ AUCUN appelant ⛔ ne le lui transmettait. Son
  //    filtrage ⛔ ne pouvait donc JAMAIS s'appliquer depuis la configuration :
  //    une mécanique présente et INERTE, exactement le motif « code présent,
  //    effet nul ».
  // 🔑 On transmet `parcoursCreation` TEL QUEL : le parcours déclaré par le
  //    tenant EST le profil de l'assistant. ⛔ Aucune traduction, ⛔ aucune
  //    table de correspondance à tenir à jour.
  // ⛔ TEF EST INCHANGÉ, et c'est vérifiable : `profile="tef_amo"` sélectionne
  //    les MÊMES quatre étapes que le repli historique (`profile=null`).
  if (parcoursCreation === "tef_amo" || parcoursCreation === "b2c") {
    return (
      <CreateDossierTefWizard
        tenantId={tenantId}
        profile={parcoursCreation}
        activite={activité || (hasTEF ? "tef" : (hasClimat ? "climat" : null))}
        civiliteOptions={civiliteOptions}
        onBack={onBack}
        onSuccess={onSuccess}
      />
    );
  }

  // 🔴 FAIL-CLOSED — ⛔ AUCUN parcours choisi à la place du métier.
  // ⚠️ C'est LE point du correctif : avant, ce cas ⛔ n'existait PAS — tout
  // tombait dans l'une des deux branches. Un tenant sans parcours déclaré
  // recevait donc le métier d'un autre, EN SILENCE.
  if (parcoursCreation !== "omv") {
    return (
      <div style={{ background: "var(--oh-bg-elevated)",
                    border: "0.5px solid var(--oh-border)",
                    borderRadius: 12, padding: 16 }}
           data-testid="creation-non-configuree">
        <div style={{ color: "var(--oh-text)", fontWeight: 500, marginBottom: 8 }}>
          Parcours de création non configuré
        </div>
        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                      lineHeight: 1.6 }}>
          Ce métier n'a pas encore de parcours de création de dossier déclaré.
          Aucun parcours d'un autre métier n'est appliqué à sa place.
          <br />
          À renseigner dans la configuration du tenant
          (<code>config.dossier_creation_profile</code>), puis rouvrir cet écran.
        </div>
        {onBack && (
          <button onClick={onBack} data-testid="creation-non-configuree-retour"
                  style={{ marginTop: 14, height: 36, borderRadius: 8,
                           border: "1px solid var(--oh-border)",
                           background: "transparent",
                           color: "var(--oh-text-secondary)", padding: "0 14px" }}>
            Retour
          </button>
        )}
      </div>
    );
  }

  // ─── ÉCRAN DE CRÉATION OMV (épuré) : recherche par nom OU création par SIREN ───
  // 🔑 Atteint UNIQUEMENT si le tenant a DÉCLARÉ le parcours « omv ».
  // ⚠️ `isOMV` reste utilisé PLUS BAS pour le comportement de SAISIE (SIREN,
  // entreprise) : c'est bien le BUSINESS MODEL qui le pilote là — ⛔ pas le
  // parcours. Les deux notions restent distinctes, et c'est voulu.
  if (parcoursCreation === "omv") {
    const hasEntreprise = !!(omv.entreprise.raison_sociale || omv.entreprise.siret);
    return (
      <div className="max-w-2xl w-full mx-auto space-y-6 px-4 sm:px-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Retour" className="text-[var(--oh-text-muted)]"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="min-w-0">
            <h1 className="text-xl font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}><FolderPlus className="w-6 h-6 shrink-0" style={{ color: "var(--oh-accent-text)" }} /> Nouveau dossier OMV</h1>
            <p className="text-[var(--oh-text-secondary)] text-sm">Rechercher une entreprise en base, ou la créer via son SIREN/SIRET. Le reste (n° dossier, représentant, bâtiment, VMC, CEE) se remplit dans la fiche.</p>
          </div>
        </div>

        {/* 0. Import formulaire CEE (Phase 1 : PDF natif) — pré-remplit la fiche */}
        <OhCard>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-medium mb-1 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--oh-accent-text)" }} /> Importer un formulaire CEE
              </h3>
              <p className="text-[var(--oh-text-secondary)] text-xs">
                Lis un formulaire de demande CEE (PDF natif) et pré-remplit les champs.
                L'entreprise est récupérée via le SIREN ; les autres champs sont à confirmer.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="hidden"
              data-testid="omv-import-file"
              onChange={(e) => handleImportForm(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              style={btnSecondary}
              className="shrink-0"
              data-testid="omv-import-btn"
            >
              {importing
                ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyse…</>)
                : (<><Upload className="w-4 h-4 mr-2" /> Importer un formulaire</>)}
            </Button>
          </div>

          {importInfo?.type === "scan" && (
            <OhStatusNote variant="info" className="mt-3" style={{ fontSize: 12 }} data-testid="omv-import-scan">
              Document scanné/manuscrit détecté — l'extraction visuelle arrivera en Phase 2.
              Saisie manuelle pour l'instant.
            </OhStatusNote>
          )}
          {importInfo?.type === "ok" && (
            <div
              className="mt-3 p-3"
              style={{
                background: "var(--oh-bg-surface)",
                border: "0.5px solid var(--oh-border)",
                borderRadius: "var(--oh-radius-control)",
              }}
              data-testid="omv-import-preview"
            >
              <p className="text-xs mb-2" style={{ color: "var(--oh-success-text)" }}>
                {importInfo.count} champ(s) extrait(s) — à confirmer dans la fiche après création.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {importInfo.fields.map((f, i) => (
                  <span key={i}
                    className="text-[11px] px-2 py-0.5 rounded border"
                    style={f.confirmed
                      ? { background: "var(--oh-success-bg)", borderColor: "var(--oh-success-border)", color: "var(--oh-success-text)" }
                      : { background: "var(--oh-info-bg)", borderColor: "var(--oh-info-border)", color: "var(--oh-info-text)" }}
                    title={f.confirmed ? "Confirmé (SIREN)" : "À confirmer"}>
                    {f.label}: {f.value}{f.confirmed ? "" : " ·à confirmer"}
                  </span>
                ))}
              </div>
            </div>
          )}
        </OhCard>

        {/* 1. Recherche par NOM d'entreprise (base) */}
        <OhCard>
          <OhCardHeader title="Rechercher une entreprise (base)" />
          {selectedClient ? (
            <div
              className="flex items-center justify-between gap-3 p-4 rounded-lg"
              style={{ background: "var(--oh-accent-bg)", border: "1px solid var(--oh-accent)" }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="w-5 h-5 shrink-0" style={{ color: "var(--oh-accent-text)" }} />
                <p className="font-medium truncate" style={{ color: "var(--oh-accent-text)" }}>{selectedClient.display_name || formatClientFullName(selectedClient)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)} style={{ color: "var(--oh-text-secondary)" }} className="shrink-0">Changer</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
                <Input placeholder="Nom de l'entreprise..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} className="pl-10 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]" data-testid="omv-search-input" />
              </div>
              {searchResults.length > 0 && (
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg divide-y divide-[var(--oh-border)]">
                  {searchResults.map((client) => (
                    <Button
                      key={client.id}
                      variant="ghost"
                      onClick={() => { setSelectedClient(client); setSearchQuery(""); setSearchResults([]); }}
                      className="w-full h-auto min-h-[44px] justify-start gap-3 p-3 rounded-none text-left hover:bg-[var(--oh-bg-hover)]"
                      data-testid="omv-search-result"
                    >
                      <Building2 className="w-4 h-4 shrink-0" style={{ color: "var(--oh-text-secondary)" }} />
                      <div className="min-w-0">
                        <p className="text-sm truncate" style={{ color: "var(--oh-text)" }}>{client.display_name || client.raison_sociale || `${client.nom} ${client.prenom || ''}`}</p>
                        <p className="text-xs truncate font-normal" style={{ color: "var(--oh-text-secondary)" }}>{client.siret || client.ville || client.email || ""}</p>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </OhCard>

        {/* 2. Création entreprise par SIREN (DÉCOUPLÉ — toujours saisissable) */}
        {!selectedClient && (
          <OhCard>
            <OhCardHeader
              title="Ou créer l'entreprise bénéficiaire"
              description="SIREN/SIRET (auto-remplissage API gouv) — ou saisie manuelle si introuvable."
            />
            <div className="mb-3">
              <OhField label="SIREN / SIRET" htmlFor="omv-siret" error={siretError}>
                <div className="relative">
                  <OhInput
                    id="omv-siret"
                    type="text"
                    value={omv.entreprise.siret ?? ""}
                    onChange={(e) => handleOmvSiretChange(e.target.value)}
                    placeholder="9 chiffres (SIREN) ou 14 (SIRET)"
                    invalid={!!siretError}
                    style={{ paddingRight: "var(--oh-space-8, 2rem)" }}
                    data-testid="omv-siret-input"
                    aria-invalid={!!siretError}
                  />
                  {siretSearching && <RefreshCw className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--oh-text-secondary)" }} />}
                </div>
              </OhField>
              {siretError && <span className="sr-only" data-testid="omv-siret-error">{siretError}</span>}
              {siretInfo && !siretError && <p className="text-xs mt-1" style={{ color: siretInfo.found ? "var(--oh-success-text)" : "var(--oh-info-text)" }}>{siretInfo.message}{siretInfo.found && siretInfo.siege_adresse ? ` — ${siretInfo.siege_adresse}` : ""}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <OmvInput label="Raison sociale" value={omv.entreprise.raison_sociale} onChange={(v) => setOmvField("entreprise", "raison_sociale", v)} />
              {/* Secteur d'activité : liste éditable per-tenant (Variables et Listes). */}
              <OhField label="Secteur d'activité">
                <OhSelect
                  value={omv.entreprise.secteur_activite ?? ""}
                  onChange={(e) => setOmvField("entreprise", "secteur_activite", e.target.value)}
                  data-testid="omv-secteur-select"
                >
                  <option value="">— Choisir —</option>
                  {(secteurOptions || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.label || s.id}</option>
                  ))}
                </OhSelect>
              </OhField>
              <OmvInput label="Adresse" value={omv.entreprise.adresse} onChange={(v) => setOmvField("entreprise", "adresse", v)} />
              <OmvInput label="Code postal" value={omv.entreprise.cp} onChange={(v) => setOmvField("entreprise", "cp", v)} />
              <OmvInput label="Ville" value={omv.entreprise.ville} onChange={(v) => setOmvField("entreprise", "ville", v)} />
            </div>
          </OhCard>
        )}

        {createError && (
          <OhStatusNote variant="error" data-testid="omv-create-error">{createError}</OhStatusNote>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button variant="outline" onClick={onBack} style={btnDiscret} className="min-h-[44px] w-full sm:w-auto" data-testid="omv-cancel-btn">Annuler</Button>
          <Button variant="outline" onClick={() => handleCreateDossier(false)} disabled={creating || (!selectedClient && !hasEntreprise)} style={btnPrimary} className="min-h-[44px] w-full sm:w-auto" data-testid="omv-create-dossier-btn">
            {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : (<><FolderPlus className="w-4 h-4 mr-2" /> Créer le dossier</>)}
          </Button>
        </div>

        {/* Dialog de détection doublon (client + dossier) — DOIT être monté ici :
            la branche OMV fait un early-return, le dialog du return principal
            (plus bas) est donc inatteignable. Sans ce montage, un 409 dédup SIRET
            (client orphelin au réimport) posait setDuplicateDialog mais rien ne
            s'affichait. Mêmes props que le return principal. */}
        {/* profil installateur/OMV : client = dossier (1:1) → vocabulaire "dossier" */}
        <ClientDuplicateDialog
          open={duplicateDialog.open}
          mode={duplicateDialog.mode}
          matches={duplicateDialog.matches}
          entity="dossier"
          title={duplicateDialog.title}
          description={duplicateDialog.description}
          createLabel={duplicateDialog.createLabel}
          useLabel={duplicateDialog.useLabel}
          onUseExisting={duplicateDialog.onUseExisting}
          onCreateAnyway={() => {
            const fn = duplicateDialog.onConfirm;
            setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
            fn?.();
          }}
          onCancel={() => setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null })}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl w-full mx-auto space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} aria-label="Retour" className="text-[var(--oh-text-muted)]">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
            <FolderPlus className="w-6 h-6 shrink-0" style={{ color: "var(--oh-accent-text)" }} />
            Nouveau dossier
          </h1>
          <p className="text-[var(--oh-text-secondary)] text-sm">Créer un nouveau dossier TEF ou Climat</p>
        </div>
      </div>

      {/* Étape 1: Sélection Client */}
      <OhCard>
        <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <span className="w-6 h-6 rounded-full text-sm flex items-center justify-center shrink-0" style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>1</span>
          Sélectionner le client
        </h3>

        {selectedClient ? (
          <div className="flex items-center justify-between gap-3 p-4 rounded-lg" style={{ background: "var(--oh-accent-bg)", border: "1px solid var(--oh-accent)" }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--oh-bg-surface)" }}>
                <User className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate" style={{ color: "var(--oh-accent-text)" }}>
                  {selectedClient.display_name || formatClientFullName(selectedClient)}
                </p>
                <p className="text-sm truncate" style={{ color: "var(--oh-text-secondary)" }}>
                  {selectedClient.email || selectedClient.telephone || selectedClient.type_client}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)} style={{ color: "var(--oh-text-secondary)" }} className="shrink-0">
              Changer
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
              <Input
                placeholder="Rechercher un client existant..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
              />
            </div>

            {searchResults.length > 0 && (
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg divide-y divide-[var(--oh-border)]">
                {searchResults.map((client) => (
                  <Button
                    key={client.id}
                    variant="ghost"
                    onClick={() => {
                      setSelectedClient(client);
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                    className="w-full h-auto min-h-[44px] justify-start gap-3 p-3 rounded-none text-left hover:bg-[var(--oh-bg-hover)]"
                  >
                    <User className="w-4 h-4 shrink-0" style={{ color: "var(--oh-text-secondary)" }} />
                    <div className="min-w-0">
                      <p className="text-sm truncate" style={{ color: "var(--oh-text)" }}>
                        {client.display_name || `${client.nom} ${client.prenom || ''}`}
                      </p>
                      <p className="text-xs truncate font-normal" style={{ color: "var(--oh-text-secondary)" }}>
                        {client.email || client.telephone || (client.raison_sociale && client.type_client)}
                      </p>
                    </div>
                  </Button>
                ))}
              </div>
            )}

            <div className="text-center text-[var(--oh-text-secondary)] text-sm py-2">ou</div>

            <Button
              variant="outline"
              style={btnSecondary}
              className="w-full border-dashed min-h-[44px]"
              onClick={() => setShowNewProspectModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un nouveau prospect
            </Button>
          </div>
        )}
      </OhCard>

      {/* Étape 2: Sélection Activité */}
      <OhCard className={!selectedClient ? "opacity-50 pointer-events-none" : ""}>
        <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <span className="w-6 h-6 rounded-full text-sm flex items-center justify-center shrink-0" style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>2</span>
          Type de dossier
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {hasTEF && (
            <Button
              variant="ghost"
              onClick={() => setActivite("tef")}
              className={`h-auto flex-col items-start justify-start whitespace-normal p-6 rounded-xl border-2 transition-all text-left ${
                activité === "tef"
                  ? "border-[var(--oh-accent)] bg-[var(--oh-accent-bg)]"
                  : "border-[var(--oh-border)] hover:border-[var(--oh-border)]"
              }`}
            >
              <Building2 className={`w-8 h-8 mb-3 ${activité === "tef" ? "text-[var(--oh-accent-text)]" : "text-[var(--oh-text-secondary)]"}`} />
              <h4 className="font-medium" style={{ color: "var(--oh-text)" }}>Dossier TEF</h4>
              <p className="text-[var(--oh-text-secondary)] text-sm mt-1 font-normal">
                Parcours AMO - Conseil en rénovation
              </p>
              {activité === "tef" && (
                <Badge className="mt-3 bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border-[var(--oh-accent)]">
                  <Check className="w-3 h-3 mr-1" /> Sélectionné
                </Badge>
              )}
            </Button>
          )}

          {hasClimat && (
            <Button
              variant="ghost"
              onClick={() => setActivite("climat")}
              className={`h-auto flex-col items-start justify-start whitespace-normal p-6 rounded-xl border-2 transition-all text-left ${
                activité === "climat"
                  ? "border-[var(--oh-info-border)] bg-[var(--oh-info-bg)]"
                  : "border-[var(--oh-border)] hover:border-[var(--oh-border)]"
              }`}
            >
              <Thermometer className={`w-8 h-8 mb-3 ${activité === "climat" ? "text-[var(--oh-info-text)]" : "text-[var(--oh-text-secondary)]"}`} />
              <h4 className="font-medium" style={{ color: "var(--oh-text)" }}>Dossier Climat</h4>
              <p className="text-[var(--oh-text-secondary)] text-sm mt-1 font-normal">
                Installation PAC / Rénovation
              </p>
              {activité === "climat" && (
                <Badge className="mt-3 bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]">
                  <Check className="w-3 h-3 mr-1" /> Sélectionné
                </Badge>
              )}
            </Button>
          )}
        </div>
      </OhCard>


      {/* Étape 3: Notes */}
      <OhCard className={!selectedClient || !activité ? "opacity-50 pointer-events-none" : ""}>
        <h3 className="font-medium mb-4 flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <span className="w-6 h-6 rounded-full text-sm flex items-center justify-center shrink-0" style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>3</span>
          Notes (optionnel)
        </h3>
        <Textarea
          placeholder="Notes sur le dossier..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] min-h-[100px]"
        />
      </OhCard>

      {/* Erreur création */}
      {createError && (
        <OhStatusNote variant="error">{createError}</OhStatusNote>
      )}

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <Button variant="outline" onClick={onBack} style={btnDiscret} className="min-h-[44px] w-full sm:w-auto">
          Annuler
        </Button>
        <Button
          onClick={handleCreateDossier}
          disabled={!selectedClient || !activité || creating}
          variant="outline"
          style={btnPrimary}
          className="min-h-[44px] w-full sm:w-auto"
        >
          {creating ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <FolderPlus className="w-4 h-4 mr-2" />
              Créer le dossier
            </>
          )}
        </Button>
      </div>

      {/* Modal Création Prospect */}
      <Dialog open={showNewProspectModal} onOpenChange={setShowNewProspectModal}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
              <User className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
              Nouveau prospect
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Select 
                value={newProspect.civilite} 
                onValueChange={(v) => setNewProspect({...newProspect, civilite: v})}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {civiliteOptions.length > 0
                    ? civiliteOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)
                    : <><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></>
                  }
                </SelectContent>
              </Select>
              <Input
                placeholder="Nom *"
                value={newProspect.nom}
                onChange={(e) => setNewProspect({...newProspect, nom: e.target.value.toUpperCase()})}
                className="col-span-2 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
              />
            </div>
            
            <Input
              placeholder="Prénom"
              value={newProspect.prenom}
              onChange={(e) => setNewProspect({...newProspect, prenom: e.target.value})}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
            
            <Input
              placeholder="Email"
              type="email"
              value={newProspect.email}
              onChange={(e) => setNewProspect({...newProspect, email: e.target.value})}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
            
            <Input
              placeholder="Téléphone"
              value={newProspect.telephone}
              onChange={(e) => setNewProspect({...newProspect, telephone: e.target.value})}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
            
            <Select 
              value={newProspect.type_client} 
              onValueChange={(v) => setNewProspect({...newProspect, type_client: v})}
            >
              <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <SelectValue placeholder="Type de client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="professionnel">Professionnel</SelectItem>
                <SelectItem value="entreprise">Entreprise</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowNewProspectModal(false)}
                style={btnDiscret}
                className="min-h-[44px] w-full sm:w-auto"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateProspect}
                disabled={!newProspect.nom || creatingProspect}
                variant="outline"
                style={btnPrimary}
                className="min-h-[44px] w-full sm:w-auto"
              >
                {creatingProspect ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de détection doublon (client + dossier) */}
      <ClientDuplicateDialog
        open={duplicateDialog.open}
        mode={duplicateDialog.mode}
        matches={duplicateDialog.matches}
        onUseExisting={duplicateDialog.onUseExisting}
        onCreateAnyway={() => {
          const fn = duplicateDialog.onConfirm;
          setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null });
          fn?.();
        }}
        onCancel={() => setDuplicateDialog({ open: false, mode: "client", matches: [], onConfirm: null })}
      />
    </div>
  );
}
